import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, FileText, User, BookOpen, 
  CheckCircle, Clock, Send, Loader2, Download, Building2, Users, Eye
} from 'lucide-react';
import { PifRecapTable } from '../components/pif/PifRecapTable';
const DEFAULT_TOTAL_STAGE = 496;

// Intitulés officiels des BC
const BC_INTITULES = {
  bc1: "BC1 : Concevoir et mettre en œuvre des projets d'animation dans le cadre de l'organisation de travail d'une structure du champ du sport ou de l'animation",
  bc2: "BC2 : Valoriser les activités et les projets d'une structure du sport ou de l'animation",
  bc3: "BC3 : Concevoir, conduire en sécurité et évaluer des séances et des cycles de découverte, d'initiation et d'apprentissage des activités de motonautisme et des disciplines associées dans le cadre du projet et de l'organisation de travail de la structure"
};

// Composant pour une section BC - EXTRAIT pour éviter le re-mount à chaque render
const BCSection = ({ bcKey, title, bcData, onBcChange }) => {
  return (
    <Card className="border-l-4 border-l-[#8DC63F]">
      <CardHeader className="bg-[#8DC63F]/10 py-3">
        <CardTitle className="text-sm font-semibold text-slate-800">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Heures de formation */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Formation (heures)</Label>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Complète</Label>
              <Input
                data-testid={`${bcKey}-heures-complete`}
                type="number"
                min="0"
                value={bcData.heures_complete || 0}
                onChange={(e) => onBcChange(bcKey, 'heures_complete', parseInt(e.target.value) || 0)}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Allégée (-)</Label>
              <Input
                data-testid={`${bcKey}-heures-allegee`}
                type="number"
                min="0"
                max={bcData.heures_complete || 0}
                value={bcData.heures_allegee || 0}
                onChange={(e) => onBcChange(bcKey, 'heures_allegee', Math.min(parseInt(e.target.value) || 0, bcData.heures_complete || 0))}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Renforcée (+)</Label>
              <Input
                data-testid={`${bcKey}-heures-renforcee`}
                type="number"
                min="0"
                value={bcData.heures_renforcee || 0}
                onChange={(e) => onBcChange(bcKey, 'heures_renforcee', parseInt(e.target.value) || 0)}
                className="text-sm"
              />
            </div>
          </div>
        </div>
        
        {/* Observations - Allègements */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Observations</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bcData.allegement_autre_bpjeps || false}
                onChange={(e) => onBcChange(bcKey, 'allegement_autre_bpjeps', e.target.checked)}
                className="w-4 h-4 text-[#8DC63F] rounded"
              />
              <span>Dispense dû à un autre BPJEPS ou diplôme</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bcData.allegement_parcours_pro || false}
                onChange={(e) => onBcChange(bcKey, 'allegement_parcours_pro', e.target.checked)}
                className="w-4 h-4 text-[#8DC63F] rounded"
              />
              <span>Allégement dû au parcours professionnel</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bcData.aucun_allegement || false}
                onChange={(e) => onBcChange(bcKey, 'aucun_allegement', e.target.checked)}
                className="w-4 h-4 text-[#8DC63F] rounded"
              />
              <span>Aucun allègement</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bcData.refus_allegement || false}
                onChange={(e) => onBcChange(bcKey, 'refus_allegement', e.target.checked)}
                className="w-4 h-4 text-[#8DC63F] rounded"
              />
              <span>Refus de l'allègement par le candidat</span>
            </label>
          </div>
        </div>
        
        {/* VAE et Dispense */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm">Orientation vers demande de dossier VAE</Label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`${bcKey}_vae`}
                  checked={bcData.orientation_vae === true}
                  onChange={() => onBcChange(bcKey, 'orientation_vae', true)}
                  className="w-4 h-4 text-[#8DC63F]"
                />
                <span className="text-sm">OUI</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`${bcKey}_vae`}
                  checked={bcData.orientation_vae === false}
                  onChange={() => onBcChange(bcKey, 'orientation_vae', false)}
                  className="w-4 h-4 text-[#8DC63F]"
                />
                <span className="text-sm">NON</span>
              </label>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Dispense de certification</Label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`${bcKey}_dispense`}
                  checked={bcData.dispense_certification === true}
                  onChange={() => onBcChange(bcKey, 'dispense_certification', true)}
                  className="w-4 h-4 text-[#8DC63F]"
                />
                <span className="text-sm">OUI</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`${bcKey}_dispense`}
                  checked={bcData.dispense_certification === false}
                  onChange={() => onBcChange(bcKey, 'dispense_certification', false)}
                  className="w-4 h-4 text-[#8DC63F]"
                />
                <span className="text-sm">NON</span>
              </label>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Section FOAD - EXTRAITE pour éviter le re-mount
const FOADSection = ({ foadData, onBcChange }) => {
  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardHeader className="bg-blue-50 py-3">
        <CardTitle className="text-sm font-semibold text-slate-800">
          FOAD (Formation Ouverte et à Distance)
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Observations</Label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="foad-dispose-ordinateur"
              type="checkbox"
              checked={foadData.dispose_ordinateur || false}
              onChange={(e) => onBcChange('foad', 'dispose_ordinateur', e.target.checked)}
              className="w-4 h-4 text-[#8DC63F] rounded"
            />
            <span className="text-sm">Dispose d'un ordinateur ou tablette et connexion internet</span>
          </label>
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm font-medium">Formation (heures)</Label>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Complète</Label>
              <Input
                data-testid="foad-heures-complete"
                type="number"
                min="0"
                value={foadData.heures_complete || 0}
                onChange={(e) => onBcChange('foad', 'heures_complete', parseInt(e.target.value) || 0)}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Allégée (-)</Label>
              <Input
                data-testid="foad-heures-allegee"
                type="number"
                min="0"
                max={foadData.heures_complete || 0}
                value={foadData.heures_allegee || 0}
                onChange={(e) => onBcChange('foad', 'heures_allegee', Math.min(parseInt(e.target.value) || 0, foadData.heures_complete || 0))}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Renforcée (+)</Label>
              <Input
                data-testid="foad-heures-renforcee"
                type="number"
                min="0"
                value={foadData.heures_renforcee || 0}
                onChange={(e) => onBcChange('foad', 'heures_renforcee', parseInt(e.target.value) || 0)}
                className="text-sm"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PIFCandidat = ({ user }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const candidatId = searchParams.get('id');
  
  const [candidat, setCandidat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [existingPif, setExistingPif] = useState(null);
  const [centreProfiles, setCentreProfiles] = useState({});
  const [selectedCentre, setSelectedCentre] = useState('atlantique');
  const [pifSaved, setPifSaved] = useState(false); // Pour savoir si on peut voir/envoyer
  
  // État du formulaire PIF - Structure avec BC1, BC2, BC3, FOAD (pas de BC4)
  const [pifData, setPifData] = useState({
    centre: 'atlantique',
    bc1: {
      intitule: BC_INTITULES.bc1,
      heures_complete: 0,
      heures_allegee: 0,
      heures_renforcee: 0,
      allegement_autre_bpjeps: false,
      allegement_parcours_pro: false,
      aucun_allegement: true,
      refus_allegement: false,
      orientation_vae: false,
      dispense_certification: false
    },
    bc2: {
      intitule: BC_INTITULES.bc2,
      heures_complete: 0,
      heures_allegee: 0,
      heures_renforcee: 0,
      allegement_autre_bpjeps: false,
      allegement_parcours_pro: false,
      aucun_allegement: true,
      refus_allegement: false,
      orientation_vae: false,
      dispense_certification: false
    },
    bc3: {
      intitule: BC_INTITULES.bc3,
      heures_complete: 0,
      heures_allegee: 0,
      heures_renforcee: 0,
      allegement_autre_bpjeps: false,
      allegement_parcours_pro: false,
      aucun_allegement: true,
      refus_allegement: false,
      orientation_vae: false,
      dispense_certification: false
    },
    foad: {
      intitule: 'Formation Ouverte et à Distance',
      heures_complete: 0,
      heures_allegee: 0,
      heures_renforcee: 0,
      dispose_ordinateur: true
    },
    // Formateurs à la fin (jusqu'à 3)
    formateurs: ['', '', ''],
    total_heures_stage: DEFAULT_TOTAL_STAGE,
    total_allegements: 0,
    total_heures_formation: 0
  });

  useEffect(() => {
    if (candidatId) {
      loadData();
    }
  }, [candidatId]);

  // Quand le centre change, mettre à jour les heures BC depuis heures_defaut
  useEffect(() => {
    if (centreProfiles && selectedCentre) {
      const centreKeys = Object.keys(centreProfiles);
      const matchingKey = centreKeys.find(k => k.toLowerCase() === selectedCentre.toLowerCase());
      
      if (matchingKey && centreProfiles[matchingKey]) {
        const profile = centreProfiles[matchingKey];
        // Support both old format (heures_defaut) and new format (heures_bc1, etc.)
        const heuresDefaut = profile.heures_defaut || {};
        
        // Heures de stage depuis profil centre
        const heuresStage = parseInt(profile.duree_totale_stage) || DEFAULT_TOTAL_STAGE;
        
        setPifData(prev => ({
          ...prev,
          centre: selectedCentre,
          bc1: {
            ...prev.bc1,
            intitule: profile.nom_bc1 || BC_INTITULES.bc1,
            heures_complete: parseInt(profile.heures_bc1) || parseInt(heuresDefaut.bc1) || 155
          },
          bc2: {
            ...prev.bc2,
            intitule: profile.nom_bc2 || BC_INTITULES.bc2,
            heures_complete: parseInt(profile.heures_bc2) || parseInt(heuresDefaut.bc2) || 205
          },
          bc3: {
            ...prev.bc3,
            intitule: profile.nom_bc3 || BC_INTITULES.bc3,
            heures_complete: parseInt(profile.heures_bc3) || parseInt(heuresDefaut.bc3) || 105
          },
          foad: {
            ...prev.foad,
            heures_complete: parseInt(profile.heures_foad) || parseInt(heuresDefaut.foad) || 55
          },
          total_heures_stage: heuresStage
        }));
      }
    }
  }, [selectedCentre, centreProfiles]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les infos du candidat
      const candidatRes = await axios.get(`/candidats/${candidatId}`);
      setCandidat(candidatRes.data);
      
      // Charger les profils des centres
      try {
        const centresRes = await axios.get('/centres-profiles');
        if (centresRes.data) {
          setCentreProfiles(centresRes.data);
          const candidatCentre = candidatRes.data.centre || 'atlantique';
          setSelectedCentre(candidatCentre.toLowerCase());
        }
      } catch (err) {
      }
      
      // Charger le PIF existant s'il y en a un
      try {
        const pifRes = await axios.get(`/candidats/${candidatId}/pif`);
        if (pifRes.data) {
          setExistingPif(pifRes.data);
          setPifSaved(true);
          setPifData(prev => ({
            ...prev,
            ...pifRes.data,
            bc1: { ...prev.bc1, ...pifRes.data.bc1, intitule: BC_INTITULES.bc1 },
            bc2: { ...prev.bc2, ...pifRes.data.bc2, intitule: BC_INTITULES.bc2 },
            bc3: { ...prev.bc3, ...pifRes.data.bc3, intitule: BC_INTITULES.bc3 },
            foad: { ...prev.foad, ...pifRes.data.foad },
            formateurs: pifRes.data.formateurs || ['', '', '']
          }));
          if (pifRes.data.centre) {
            setSelectedCentre(pifRes.data.centre.toLowerCase());
          }
        }
      } catch (err) {
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleBcChange = (bcKey, field, value) => {
    setPifData(prev => ({
      ...prev,
      [bcKey]: {
        ...prev[bcKey],
        [field]: value
      }
    }));
    setPifSaved(false); // Marquer comme non sauvegardé
  };

  const handleFormateurChange = (index, value) => {
    setPifData(prev => {
      const newFormateurs = [...prev.formateurs];
      newFormateurs[index] = value;
      return { ...prev, formateurs: newFormateurs };
    });
    setPifSaved(false);
  };

  // Formation = BC1+BC2+BC3 (complète) - allégements ; FOAD à part (allégée) ; total = stage + formation + FOAD
  const calculateTotals = () => {
    const h = (v) => Math.max(parseInt(v) || 0, 0);
    const net = (k) => h(pifData[k]?.heures_complete) - Math.min(h(pifData[k]?.heures_allegee), h(pifData[k]?.heures_complete));
    const totalHeures = ['bc1', 'bc2', 'bc3'].reduce((s, k) => s + h(pifData[k]?.heures_complete), 0);
    const totalFormation = ['bc1', 'bc2', 'bc3'].reduce((s, k) => s + net(k), 0);
    const totalFoad = net('foad');
    const totalAllegements = totalHeures - totalFormation + h(pifData.foad?.heures_complete) - totalFoad;
    return { totalHeures, totalFormation, totalFoad, totalAllegements };
  };

  // Enregistrer le PIF (sans envoyer)
  const handleSavePIF = async () => {
    setSaving(true);
    try {
      const { totalFormation, totalAllegements } = calculateTotals();
      
      const dataToSend = {
        ...pifData,
        centre: selectedCentre,
        total_allegements: totalAllegements,
        total_heures_formation: totalFormation,
        send_to_candidat: false // Ne pas envoyer, juste sauvegarder
      };
      
      await axios.post(`/candidats/${candidatId}/pif`, dataToSend);
      toast.success('Plan Individuel de Formation enregistré');
      setPifSaved(true);
      
      // Recharger pour avoir l'ID
      const pifRes = await axios.get(`/candidats/${candidatId}/pif`);
      if (pifRes.data) {
        setExistingPif(pifRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Envoyer le PIF au candidat
  const handleSendPIF = async () => {
    setSending(true);
    try {
      const { totalFormation, totalAllegements } = calculateTotals();
      
      const dataToSend = {
        ...pifData,
        centre: selectedCentre,
        total_allegements: totalAllegements,
        total_heures_formation: totalFormation,
        send_to_candidat: true // Envoyer au candidat
      };
      
      await axios.post(`/candidats/${candidatId}/pif`, dataToSend);
      toast.success('Plan Individuel de Formation envoyé au candidat !');
      
      // Recharger les données
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  // Voir le PDF (prévisualisation)
  const handleViewPDF = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/pif/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du PDF. Enregistrez d\'abord le PIF.');
    }
  };

  // Télécharger le PDF
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/pif/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${candidat?.nom || ''}_${candidat?.prenom || ''}_PIF.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  if (!candidat) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Candidat non trouvé</p>
        <Link to="/gestion-candidats">
          <Button variant="link">Retour à la liste</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pif-candidat-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8DC63F] to-[#6ba32e] text-white rounded-lg p-6 shadow-md">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to={`/fiche-candidat?id=${candidatId}`}>
              <Button variant="outline" size="sm" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Retour
              </Button>
            </Link>
            
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/50">
              <span className="text-xl font-bold">
                {candidat.prenom?.[0]}{candidat.nom?.[0]}
              </span>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-white/20">Plan Individuel de Formation</Badge>
                {existingPif && (
                  <Badge className={existingPif.statut === 'signe' ? 'bg-green-500' : existingPif.statut === 'en_attente_signature' ? 'bg-amber-500' : 'bg-slate-500'}>
                    {existingPif.statut === 'signe' ? 'Signé' : existingPif.statut === 'en_attente_signature' ? 'En attente de signature' : 'Brouillon'}
                  </Badge>
                )}
              </div>
              <h1 className="text-xl md:text-2xl font-bold">
                {candidat.prenom} {candidat.nom}
              </h1>
            </div>
          </div>
          
          {existingPif && (
            <Button 
              onClick={handleDownloadPDF}
              className="bg-white text-[#8DC63F] hover:bg-white/90"
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger PDF
            </Button>
          )}
        </div>
      </div>

      {/* Sélection du Centre */}
      <Card className="border-2 border-blue-500">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Centre de Formation
          </CardTitle>
          <CardDescription>
            Sélectionnez le centre pour charger automatiquement les heures par BC
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3">
            {Object.keys(centreProfiles).filter(k => !['type', 'updated_at', 'sites', '_id'].includes(k.toLowerCase())).map((centreKey) => (
              <button
                key={centreKey}
                onClick={() => setSelectedCentre(centreKey.toLowerCase())}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  selectedCentre === centreKey.toLowerCase()
                    ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                    : 'border-slate-200 hover:border-blue-300'
                }`}
              >
                {centreKey.charAt(0).toUpperCase() + centreKey.slice(1)}
              </button>
            ))}
          </div>
          {(() => {
            const centreKeys = Object.keys(centreProfiles);
            const matchingKey = centreKeys.find(k => k.toLowerCase() === selectedCentre.toLowerCase());
            const profile = matchingKey ? centreProfiles[matchingKey] : null;
            return profile && (
              <p className="mt-3 text-sm text-slate-600">
                Formation : {profile?.intitule_formation || 'BPJEPS'}
              </p>
            );
          })()}
        </CardContent>
      </Card>

      {/* Statut PIF existant */}
      {existingPif && existingPif.statut === 'en_attente_signature' && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-amber-500" />
              <div>
                <p className="font-medium">PIF envoyé au candidat - En attente de signature</p>
                <p className="text-sm text-slate-500">
                  Créé le {new Date(existingPif.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {existingPif && existingPif.statut === 'signe' && (
        <Card className="border-l-4 border-l-green-500 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-500" />
              <div>
                <p className="font-medium">PIF signé par le candidat le {existingPif.date_signature}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sections BC (BC1, BC2, BC3 - pas de BC4) */}
      <div className="space-y-4">
        <BCSection 
          bcKey="bc1" 
          title={BC_INTITULES.bc1}
          bcData={pifData.bc1 || {}}
          onBcChange={handleBcChange}
        />
        
        <BCSection 
          bcKey="bc2" 
          title={BC_INTITULES.bc2}
          bcData={pifData.bc2 || {}}
          onBcChange={handleBcChange}
        />
        
        <BCSection 
          bcKey="bc3" 
          title={BC_INTITULES.bc3}
          bcData={pifData.bc3 || {}}
          onBcChange={handleBcChange}
        />
        
        <FOADSection 
          foadData={pifData.foad || {}}
          onBcChange={handleBcChange}
        />
      </div>

      {/* Section Formateurs - À la fin */}
      <Card className="border-2 border-purple-500">
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            Formateurs
          </CardTitle>
          <CardDescription>
            Renseignez les formateurs (jusqu'à 3)
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="space-y-1">
              <Label className="text-sm text-slate-500">Formateur {index + 1}</Label>
              <Input
                value={pifData.formateurs[index] || ''}
                onChange={(e) => handleFormateurChange(index, e.target.value)}
                placeholder={`Nom du formateur ${index + 1}`}
                className="text-sm"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Récapitulatif */}
      <Card className="border-2 border-[#8DC63F]">
        <CardHeader className="bg-[#8DC63F]/10">
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[#8DC63F]" />
            Récapitulatif des heures
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <PifRecapTable pif={pifData} stage={pifData.total_heures_stage} />
          <p className="mt-3 text-xs text-slate-500">
            Formation = complet moins allégement, pour chaque bloc. Le devis reprend le total heures du candidat × le taux horaire.
          </p>
        </CardContent>
      </Card>

      {/* Boutons d'action en bas */}
      <div className="flex flex-wrap justify-end gap-3 p-4 bg-slate-50 rounded-lg">
        <Link to={`/fiche-candidat?id=${candidatId}`}>
          <Button variant="outline">Annuler</Button>
        </Link>
        
        {/* Bouton Enregistrer */}
        <Button 
          onClick={handleSavePIF}
          disabled={saving}
          variant="outline"
          className="border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F]/10"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Enregistrer
        </Button>
        
        {/* Bouton Voir PDF (uniquement si enregistré) */}
        <Button 
          onClick={handleViewPDF}
          disabled={!pifSaved && !existingPif}
          variant="outline"
          className="border-blue-500 text-blue-600 hover:bg-blue-50"
        >
          <Eye className="h-4 w-4 mr-2" />
          Voir PDF
        </Button>
      </div>
    </div>
  );
};

export default PIFCandidat;
